import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { TenantProvider, useTenant } from './contexts/TenantContext'
import { LanguageProvider, UserLocaleSync } from './contexts/LanguageContext'
import HomePage from './pages/HomePage'
import SignInPage from './pages/SignInPage'
import SignUpPage from './pages/SignUpPage'
import GoogleCallbackPage from './pages/GoogleCallbackPage'
import AppleCallbackPage from './pages/AppleCallbackPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import DashboardPage from './pages/DashboardPage'
import CreateSurveyPage from './pages/CreateSurveyPage'
import SurveyBuilderPage from './pages/SurveyBuilderPage'
import AdminTestPage from './pages/AdminTestPage'
import SurveyTakePage from './pages/SurveyTakePage'
import SurveySharePage from './pages/SurveySharePage'

import SurveySessionPage from './pages/SurveySessionPage'
import SurveyPreviewPage from './pages/SurveyPreviewPage'
import SurveyResponsesPage from './pages/SurveyResponsesPage'
import EditSurveyPage from './pages/EditSurveyPage'
import AcceptInvitationPage from './pages/AcceptInvitationPage'
import StyleGuidePage from './pages/StyleGuidePage'
import SurveyTemplatesPage from './pages/SurveyTemplatesPage'
import ImpactReportPage from './pages/ImpactReportPage'
import NotificationSettingsPage from './pages/NotificationSettingsPage'
import CompliancePage from './pages/CompliancePage'
import { OrganizationList } from './components/organizations/OrganizationList'
import { CreateOrganization } from './components/organizations/CreateOrganization'
import OrganizationDetailPage from './pages/OrganizationDetailPage'
import ApiDocsPage from './pages/ApiDocsPage'
import DevelopersPage from './pages/DevelopersPage'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-foreground-muted">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/auth/signin" replace />
  }

  return <>{children}</>
}

// Admin Route — blocks respondents from admin-only pages
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading, isAdmin } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-foreground-muted">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/auth/signin" replace />
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

/**
 * Home route on a tenant subdomain (e.g. happiness.wehappers.org) shouldn't
 * show Sondely marketing — it should drop the user into their org. If
 * unauthenticated, send to signin so the post-login redirect lands on the
 * org page. This component runs *after* TenantProvider has resolved.
 */
const TenantAwareHome: React.FC = () => {
  const { tenant, isLoading: tenantLoading } = useTenant()
  const { user, isLoading: authLoading } = useAuth()

  if (tenantLoading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    )
  }

  if (tenant) {
    return user
      ? <Navigate to={`/organizations/${tenant.id}`} replace />
      : <Navigate to="/auth/signin" replace />
  }

  return <HomePage />
}

// Public Route component (redirect to dashboard if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-foreground-muted">Loading...</p>
        </div>
      </div>
    )
  }

  if (user) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
      <TenantProvider>
        <AuthProvider>
        <UserLocaleSync />
        <Router>
          <div className="min-h-screen bg-background">
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<TenantAwareHome />} />
              <Route
                path="/auth/signin"
                element={
                  <PublicRoute>
                    <SignInPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/auth/signup"
                element={
                  <PublicRoute>
                    <SignUpPage />
                  </PublicRoute>
                }
              />

              <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
              <Route path="/auth/apple/callback" element={<AppleCallbackPage />} />
              <Route
                path="/forgot-password"
                element={
                  <PublicRoute>
                    <ForgotPasswordPage />
                  </PublicRoute>
                }
              />
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              {/* Invitation acceptance (works both logged-in and logged-out) */}
              <Route path="/invitations/accept" element={<AcceptInvitationPage />} />

              {/* Public survey taking route */}
              <Route path="/survey/:publicId" element={<SurveyTakePage />} />

              {/* Protected routes (accessible to all authenticated users) */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/organizations"
                element={
                  <ProtectedRoute>
                    <OrganizationList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/organizations/:id"
                element={
                  <ProtectedRoute>
                    <OrganizationDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/notifications"
                element={
                  <ProtectedRoute>
                    <NotificationSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/surveys/:surveyId/responses"
                element={
                  <ProtectedRoute>
                    <SurveyResponsesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/surveys/:surveyId/take"
                element={
                  <ProtectedRoute>
                    <SurveySessionPage />
                  </ProtectedRoute>
                }
              />

              {/* Admin-only routes (blocked for respondents) */}
              <Route
                path="/organizations/new"
                element={
                  <AdminRoute>
                    <CreateOrganization />
                  </AdminRoute>
                }
              />
              <Route
                path="/organizations/:orgId/surveys/:surveyId/compliance"
                element={
                  <AdminRoute>
                    <CompliancePage />
                  </AdminRoute>
                }
              />
              <Route
                path="/surveys/templates"
                element={
                  <AdminRoute>
                    <SurveyTemplatesPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/impact"
                element={
                  <AdminRoute>
                    <ImpactReportPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/surveys/:surveyId/impact"
                element={
                  <AdminRoute>
                    <ImpactReportPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/surveys/new"
                element={
                  <AdminRoute>
                    <SurveyBuilderPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/surveys/new/classic"
                element={
                  <AdminRoute>
                    <CreateSurveyPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/surveys/:surveyId/share"
                element={
                  <AdminRoute>
                    <SurveySharePage />
                  </AdminRoute>
                }
              />
              <Route
                path="/surveys/:surveyId/edit"
                element={
                  <AdminRoute>
                    <SurveyBuilderPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/surveys/:surveyId/edit/classic"
                element={
                  <AdminRoute>
                    <EditSurveyPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/surveys/:surveyId/test"
                element={
                  <AdminRoute>
                    <AdminTestPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/surveys/:surveyId/preview"
                element={
                  <AdminRoute>
                    <SurveyPreviewPage />
                  </AdminRoute>
                }
              />

              {/* Style Guide (dev only) */}
              <Route path="/style-guide" element={<StyleGuidePage />} />
              <Route path="/api-docs" element={<ApiDocsPage />} />
              <Route path="/developers" element={<DevelopersPage />} />

              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </Router>
        </AuthProvider>
      </TenantProvider>
      </LanguageProvider>
    </QueryClientProvider>
  )
}

export default App
